/**
 * Formatter menus and previews for any control that edits a formatter chain.
 * Framework-free, so it can be unit tested and used outside React.
 *
 * The formatters themselves (names, arguments, behaviour, menu presets) live in
 * decisively-core's registry (`FORMATTERS`), the same code that renders
 * templates, so previews here can't drift from what gets sent. This module only
 * shapes that registry into menus. A menu option is a core `FormatterPreset`:
 * its `formatter` is the string written after a `|` in a template, so one that
 * isn't in any menu (hand-written, or from an older template) still round-trips.
 */

import {
  type FormatterMenuType,
  type FormatterPreset,
  type FormatterValueType,
  formatValue,
  formattersForType,
} from "@imminently/decisively-core";
import { format } from "date-fns";

/** A value to preview formatters on, e.g. an attribute with a sample record value. */
export interface FormatterSample {
  type?: FormatterMenuType;
  sample?: string;
}

/**
 * "No formatter" in a menu. Picking it removes that formatter; it's never stored.
 * With none, the server still shows dates in the locale's short form, so this
 * isn't always the raw value.
 */
export const NONE_FORMATTER: FormatterPreset = { formatter: "none", label: "None" };

/** A type's menu: None, then every registry preset offered for that type. */
const menuFor = (type: FormatterMenuType): FormatterPreset[] => [
  NONE_FORMATTER,
  ...formattersForType(type).flatMap((definition) => definition.presets),
];

/** Default formatter menus by value type, from core's registry. A consumer can override per attribute. */
export const DEFAULT_FORMATTERS: Record<FormatterMenuType, FormatterPreset[]> = {
  text: menuFor("text"),
  date: menuFor("date"),
  number: menuFor("number"),
  boolean: menuFor("boolean"),
};

export const formattersFor = (type: FormatterMenuType): FormatterPreset[] =>
  DEFAULT_FORMATTERS[type] ?? DEFAULT_FORMATTERS.text;

/**
 * The menu for formatter number `index` in a chain. The first works on the raw
 * value, so it's the value type's menu. Every later one works on the text the
 * previous step produced, so it also offers the text formatters.
 */
export const formatterStepOptions = (base: FormatterPreset[], index: number): FormatterPreset[] => {
  if (index === 0) return base;
  const extra = DEFAULT_FORMATTERS.text.filter(
    (option) => !base.some((existing) => existing.formatter === option.formatter),
  );
  return [...base, ...extra];
};

/**
 * What "+" adds to a chain: the first formatter not already in it. The first
 * step reads the raw value, so it takes the type's menu in order; after that the
 * value is text, so text formatters come first (a second date step would do
 * nothing to "September 2026").
 */
export const nextFormatterFor = (base: FormatterPreset[], formatters: string[]): FormatterPreset | undefined => {
  const options = formatterStepOptions(base, formatters.length);
  const isText = (option: FormatterPreset) =>
    DEFAULT_FORMATTERS.text.some((text) => text.formatter === option.formatter);
  const ordered = formatters.length ? [...options.filter(isText), ...options.filter((o) => !isText(o))] : options;
  return ordered.find(
    (option) => option.formatter !== NONE_FORMATTER.formatter && !formatters.includes(option.formatter),
  );
};

/** What a menu calls a stored formatter that isn't one of its options. */
export const CUSTOM_FORMATTER_LABEL = "Custom";

/**
 * The options plus the current formatter, when it isn't one of them (e.g. a
 * hand-written `date yyyy`), labelled Custom so the menu can still show it.
 */
export const withCurrentFormatter = (options: FormatterPreset[], current: string): FormatterPreset[] =>
  !current || options.some((option) => option.formatter === current)
    ? options
    : [...options, { formatter: current, label: CUSTOM_FORMATTER_LABEL }];

/**
 * The chain to keep when a control is re-pointed at another value: all of it if
 * the new value's menu offers the first formatter (the one that reads the raw
 * value), otherwise none, since it wouldn't make sense for that type.
 */
export const keepFormattersFor = (formatters: string[], options: FormatterPreset[]): string[] =>
  formatters.length && options.some((option) => option.formatter === formatters[0]) ? formatters : [];

/** Menu labels for a chain, falling back to the raw formatter string. */
export const formatterLabels = (formatters: string[], options: FormatterPreset[]): string[] =>
  formatters.map((formatter) => options.find((option) => option.formatter === formatter)?.label ?? formatter);

/**
 * What the server would render for `value` with these formatters, via core's
 * `formatValue`. Author-facing: the locale default and relative dates depend on
 * where and when it runs.
 */
export function previewFormatters(
  value: string,
  formatters: string[],
  type?: FormatterValueType,
  today = new Date(),
): string {
  return formatValue(value, { formatters, type, now: today }) ?? "";
}

/** Formats a sample value for the preview. Empty without a sample. */
export function formatSample(sample: FormatterSample | undefined, formatters: string[], today = new Date()): string {
  if (!sample) return "";
  return previewFormatters(sample.sample ?? "", formatters, sample.type, today);
}

const EXAMPLE_VALUES: Record<FormatterMenuType, string> = {
  text: "Example text",
  date: "",
  number: "1234.5",
  boolean: "true",
};

/**
 * A value to demo formatters on in a menu: today for dates (so every date
 * formatter shows something recognisable), otherwise the sample, or a stand-in
 * for its type.
 */
export function exampleValue(sample: FormatterSample | undefined, today = new Date()): string {
  if (sample?.type === "date") return format(today, "yyyy-MM-dd");
  return sample?.sample ?? EXAMPLE_VALUES[sample?.type ?? "text"];
}
